import React from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, ArrowRight, Loader2 } from "lucide-react";
import { SectionReveal, StaggerWords } from "../components/Motion";
import { useT } from "../i18n/LanguageContext";
import ProtectedEmail from "../components/ProtectedEmail";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const initialForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  location: "",
  services: [],
  message: "",
};

const Field = ({ label, name, value, onChange, type = "text", required = false, textarea = false, testid }) => (
  <label className="block group">
    <span className="block text-[11px] uppercase tracking-[0.24em] text-[#4c535a]/60 mb-2">
      {label} {required && <span className="text-[#4c535a]">·</span>}
    </span>
    {textarea ? (
      <textarea
        data-testid={testid}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        rows={5}
        className="w-full bg-transparent border-b border-[#4c535a]/30 focus:border-[#4c535a] outline-none py-3 type-lead text-[#4c535a] resize-none placeholder:text-[#4c535a]/30 transition-colors duration-300"
      />
    ) : (
      <input
        data-testid={testid}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full bg-transparent border-b border-[#4c535a]/30 focus:border-[#4c535a] outline-none py-3 type-lead text-[#4c535a] placeholder:text-[#4c535a]/30 transition-colors duration-300"
      />
    )}
  </label>
);

export default function EnquiryPage() {
  const { t } = useT();
  const [params] = useSearchParams();
  const [form, setForm] = React.useState(initialForm);
  const [loading, setLoading] = React.useState(false);
  const [sent, setSent] = React.useState(false);

  React.useEffect(() => {
    const s = params.get("service");
    if (s && t.services.find((x) => x.id === s)) {
      const svc = t.services.find((x) => x.id === s);
      setForm((f) => ({
        ...f,
        services: [svc.name],
        message: f.message || t.enquiry.prefill_message(svc.name),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const toggleService = (name) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(name) ? f.services.filter((n) => n !== name) : [...f.services, name],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.services.length === 0) {
      toast.error(t.enquiry.error_select_service);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setSent(true);
      toast.success(t.enquiry.success_toast);
      setForm(initialForm);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t.enquiry.error_toast);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-32 md:pt-40 pb-24 md:pb-32 bg-[#EFECE6] min-h-screen" data-testid="enquiry-page">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-24">
          <p className="col-span-12 md:col-span-3 text-[11px] uppercase tracking-[0.28em] text-[#4c535a]">{t.enquiry.eyebrow}</p>
          <div className="col-span-12 md:col-span-9">
            <h1 className="font-serif type-display text-[#4c535a]">
              <StaggerWords text={t.enquiry.title_1} />
              <br />
              <StaggerWords text={t.enquiry.title_2} delay={0.15} className="italic font-light" />
            </h1>
            <p className="mt-8 max-w-xl type-body text-[#4c535a]/80">{t.enquiry.lead}</p>
          </div>
        </div>

        {sent ? (
          <SectionReveal>
            <div data-testid="enquiry-success" className="max-w-2xl border border-[#4c535a]/30 p-10 md:p-16 bg-[#E4DFD5]">
              <Check className="w-8 h-8 text-[#4c535a] mb-6" strokeWidth={1.3} />
              <h2 className="type-h1 text-[#4c535a] mb-4">
                {t.enquiry.success_title_1} <span className="italic font-light">{t.enquiry.success_title_2}</span>
              </h2>
              <p className="type-body text-[#4c535a]/80">{t.enquiry.success_body}</p>
              <button onClick={() => setSent(false)} data-testid="enquiry-send-another" className="mt-8 text-[11px] uppercase tracking-[0.28em] text-[#4c535a] link-sweep">
                {t.enquiry.send_another}
              </button>
            </div>
          </SectionReveal>
        ) : (
          <form onSubmit={submit} data-testid="enquiry-form" className="grid grid-cols-12 gap-5 md:gap-12">
            <div className="col-span-12 md:col-span-7 space-y-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Field testid="input-first-name" label={t.enquiry.first_name} name="first_name" value={form.first_name} onChange={onChange} required />
                <Field testid="input-last-name" label={t.enquiry.last_name} name="last_name" value={form.last_name} onChange={onChange} required />
              </div>
              <Field testid="input-email" label={t.enquiry.email} name="email" type="email" value={form.email} onChange={onChange} required />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Field testid="input-phone" label={t.enquiry.phone} name="phone" type="tel" value={form.phone} onChange={onChange} />
                <Field testid="input-location" label={t.enquiry.location} name="location" value={form.location} onChange={onChange} required />
              </div>
              <Field testid="input-message" label={t.enquiry.message} name="message" value={form.message} onChange={onChange} required textarea />
            </div>

            <div className="col-span-12 md:col-span-5">
              <p className="text-[11px] uppercase tracking-[0.24em] text-[#4c535a]/60 mb-4">
                {t.enquiry.services_label} <span className="text-[#4c535a]">·</span>
              </p>
              <ul className="border-t border-[#4c535a]/20">
                {t.services.map((s) => {
                  const active = form.services.includes(s.name);
                  return (
                    <li key={s.id} className="border-b border-[#4c535a]/20">
                      <button
                        type="button"
                        data-testid={`service-toggle-${s.id}`}
                        onClick={() => toggleService(s.name)}
                        className={`w-full flex items-center justify-between gap-4 py-4 text-left group transition-colors duration-300 ${
                          active ? "text-[#4c535a]" : "text-[#4c535a]"
                        }`}
                      >
                        <span className="flex items-baseline gap-3">
                          <span className="font-serif text-sm text-[#4c535a]">{s.number}</span>
                          <span className="type-lead">{s.name}</span>
                        </span>
                        <span className={`w-5 h-5 border ${active ? "bg-[#4c535a] border-[#4c535a]" : "border-[#4c535a]/40"} flex items-center justify-center transition-colors duration-300`}>
                          {active && <Check className="w-3 h-3 text-[#EFECE6]" strokeWidth={2} />}
                        </span>
                      </button>
                    </li>
                  );
                })}
                <li className="border-b border-[#4c535a]/20">
                  <button
                    type="button"
                    data-testid="service-toggle-not-sure"
                    onClick={() => toggleService(t.enquiry.not_sure)}
                    className={`w-full flex items-center justify-between gap-4 py-4 text-left transition-colors duration-300 ${
                      form.services.includes(t.enquiry.not_sure) ? "text-[#4c535a]" : "text-[#4c535a]"
                    }`}
                  >
                    <span className="flex items-baseline gap-3">
                      <span className="font-serif text-sm text-[#4c535a]">—</span>
                      <span className="type-lead">{t.enquiry.not_sure}</span>
                    </span>
                    <span className={`w-5 h-5 border ${form.services.includes(t.enquiry.not_sure) ? "bg-[#4c535a] border-[#4c535a]" : "border-[#4c535a]/40"} flex items-center justify-center transition-colors duration-300`}>
                      {form.services.includes(t.enquiry.not_sure) && <Check className="w-3 h-3 text-[#EFECE6]" strokeWidth={2} />}
                    </span>
                  </button>
                </li>
              </ul>

              <button
                type="submit"
                data-testid="enquiry-submit"
                disabled={loading}
                className="group mt-12 inline-flex items-center gap-3 w-full justify-center px-8 py-5 bg-[#4c535a] text-[#EFECE6] text-[11px] uppercase tracking-[0.28em] hover:bg-[#3d434a] disabled:opacity-60 transition-colors duration-500"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.4} /> {t.enquiry.sending}
                  </>
                ) : (
                  <>
                    {t.enquiry.submit}
                    <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1" strokeWidth={1.4} />
                  </>
                )}
              </button>
              <p className="mt-4 text-[11px] leading-[1.7] text-[#4c535a]/55">{t.enquiry.disclaimer}</p>
            </div>
          </form>
        )}

        <p className="mt-16 font-sans text-[13px] tracking-[0.04em] text-[#4c535a]/70" data-testid="enquiry-direct-email">
          {t.enquiry.or_write}{" "}
          <ProtectedEmail testId="enquiry-email-link" className="text-[#4c535a] link-sweep" />
        </p>
      </div>
    </div>
  );
}
